import {
  EditorText,
  GroupPostAttributesContentType,
  GroupPostAttributesSchedulingType,
  MediaTypePostEditorState,
  PostEditorBitly,
  PostEditorLink
} from '../../../../models/post-editor';
import { LinkGoogleCallToAction, MediaType, PostMultiMediaAttachMode, PostTiming } from '../../../../models/graphql';
import { addSeconds, isAfter, parse, setHours, setMinutes, setSeconds } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { TimezoneMap } from '../../../../constants/timezones';
import { DateStringFormat, DateTimeStringFormat } from '../../../../constants/time-formats';
import { hasLinksChecker } from '../../TextEditor/helpers/withLinks';
import { CustomParagraph } from '../../../../models/slate-editor';
import { serializeText } from '../../TextEditor/helpers/serializer';
import { getMediaConfigValidation } from './get-media-config-validation';
import {
  calculateLinkLengthPerProfile,
  CharLimitsPerNetwork,
  FirstCommentCharLimitsPerNetwork,
  getTextLength
} from '../../TextEditor/CharacterCounter/CharacterCounter.helpers';
import { CheckLinkPresentProfileCombinations } from '../check-links-present';
import { IgFbFirstCommentProfileCombinations } from '../instagram-facebook-first-comment-char-limit';

const checkExpiryCountInvalid = (scheduling: GroupPostAttributesSchedulingType) => {
  const { evergreen, postTiming } = scheduling;
  if (!evergreen.expireType.includes('count') || postTiming !== PostTiming.SCHEDULE) {
    return false;
  }
  if (evergreen.expiryMaxRepeats && evergreen.active) {
    const qntyPostsDates = scheduling.scheduledLocalDateTimes.filter(item => !item.isPast).length;
    return evergreen.expiryMaxRepeats <= qntyPostsDates;
  }
  return false;
};

const checkExpirtyDateInvalid = (scheduling: GroupPostAttributesSchedulingType) => {
  const { evergreen, postTiming, recurring, scheduledTimezone } = scheduling;
  const dateToPostSchedule = scheduling.scheduledLocalDateTimes.filter(date => !date.isPast).at(-1);
  const dateToPostRecurring = scheduling.recurring.date;
  if (
    (!recurring.expireType.includes('date') && postTiming === PostTiming.RECURRING && recurring.active) ||
    (!evergreen.expireType.includes('date') && postTiming !== PostTiming.RECURRING && evergreen.active) ||
    !scheduledTimezone
  ) {
    return false;
  }

  let isValid = true;
  const now = addSeconds(new Date(), 60);
  const tzDate = utcToZonedTime(now, TimezoneMap[scheduledTimezone]); // 23.11. 6:33 (tz)
  if (postTiming === PostTiming.SCHEDULE && dateToPostSchedule && evergreen.expiryDate && evergreen.active) {
    const parsedExpiryDate = setSeconds(
      setMinutes(setHours(parse(evergreen.expiryDate, DateStringFormat, now), 23), 59),
      59
    ); // 23.11 23:59
    const parsedPostDate = parse(dateToPostSchedule.dateTime, DateTimeStringFormat, now); // 22.11. 22:15 (CET)
    const tzParsedExpiryDate = utcToZonedTime(parsedExpiryDate, TimezoneMap[scheduledTimezone]); // 23.11. 8:30 (tz)
    isValid = isAfter(tzParsedExpiryDate, tzDate) && isAfter(parsedExpiryDate, parsedPostDate);
  } else if (postTiming === PostTiming.RECURRING && dateToPostRecurring && recurring.expiryDate && recurring.active) {
    const parsedExpiryDate = setSeconds(
      setMinutes(setHours(parse(recurring.expiryDate, DateStringFormat, now), 23), 59),
      59
    );
    const parsedPostDate = parse(dateToPostRecurring, DateTimeStringFormat, new Date());
    const tzParsedExpiryDate = utcToZonedTime(parsedExpiryDate, TimezoneMap[scheduledTimezone]);
    isValid = isAfter(tzParsedExpiryDate, tzDate) && isAfter(parsedExpiryDate, parsedPostDate);
  }
  return !isValid;
};

const checkGoogleButtonLinkInvalid = (text: EditorText, link: PostEditorLink | null) => {
  if (!link || link.googleCallToAction !== LinkGoogleCallToAction.NONE) return false;
  return !hasLinksChecker(text.text);
};

const checkLinkPreviewSuppressedInvalid = (text: EditorText, removePreview: boolean) => {
  if (!removePreview) return false;
  return !hasLinksChecker(text.text);
};

const checkLinksPresentInvalid = (
  text: EditorText,
  link: PostEditorLink | null,
  profileCombinations: CheckLinkPresentProfileCombinations
) => {
  if (!link) return false;
  return profileCombinations.includes('gmb')
    ? link.googleCallToAction === LinkGoogleCallToAction.NONE && !hasLinksChecker(text.text)
    : !hasLinksChecker(text.text);
};

const expireDateCountRequiredInvalid = (scheduling: GroupPostAttributesSchedulingType, type: 'date' | 'count') => {
  const { evergreen, postTiming, recurring } = scheduling;
  let propToCheck: 'expiryDate' | 'expiryMaxRepeats';
  if (type === 'date') {
    propToCheck = 'expiryDate';
  } else {
    propToCheck = 'expiryMaxRepeats';
  }
  return (
    (recurring.active &&
      postTiming === PostTiming.RECURRING &&
      recurring.expireType.includes(type) &&
      !recurring[propToCheck]) ||
    (evergreen.active && evergreen.expireType.includes(type) && !evergreen[propToCheck])
  );
};

const facebookContentRequiredInvalid = (
  media: MediaTypePostEditorState[],
  text: CustomParagraph[],
  link: PostEditorLink | null
) => {
  const hasLinkWithPreview = link && !link.removePreview;
  return !media.length && !serializeText(text) && !hasLinkWithPreview;
};

const facebookMediaLimitInvalid = (media: MediaTypePostEditorState[], varyMedia: PostMultiMediaAttachMode | null) => {
  const { invalidImages, invalidVideos, invalidGifs } = getMediaConfigValidation('facebook', media, varyMedia);
  return invalidImages || invalidGifs || invalidVideos;
};

const facebookTextCharLimitInvalid = (
  text: EditorText,
  link: PostEditorLink | null,
  bitlyProfile: PostEditorBitly['profilesWithBitly'][number],
  defaultBitly: PostEditorBitly['defaultBitly'],
  categoryId: string | null
) => {
  const linkLength = link ? calculateLinkLengthPerProfile(link, bitlyProfile, defaultBitly, categoryId) : 0;
  return getTextLength(text.text) + linkLength > CharLimitsPerNetwork.FacebookSocialProfile;
};

const facebookTwitterLineBreakLimitInvalid = (text: EditorText) => {
  const regex = /(\r\n|\r|\n){3,}/;
  return regex.test(serializeText(text.text));
};

const googleMediaLimitInvalid = (media: MediaTypePostEditorState[], varyMedia: PostMultiMediaAttachMode | null) => {
  const { invalidImages } = getMediaConfigValidation('google', media, varyMedia);
  return invalidImages;
};

const googleMinMediaInvalid = (postMedia: MediaTypePostEditorState[]) => {
  return postMedia.reduce((prev, current) => {
    if (prev) return prev;
    const { width, height, filesize } = current;
    if (!width || !height || !filesize) return false; // we might still wait for the data while uploading, so don't validate just yet
    return width < 250 || height < 250 || filesize < 10000; // 10.000 bytes = 10kb
  }, false);
};

const googleTextCharLimitInvalid = (
  text: EditorText,
  link: PostEditorLink | null,
  bitlyProfile: PostEditorBitly['profilesWithBitly'][number],
  defaultBitly: PostEditorBitly['defaultBitly'],
  categoryId: string | null
) => {
  const linkLength = link ? calculateLinkLengthPerProfile(link, bitlyProfile, defaultBitly, categoryId) : 0;
  return getTextLength(text.text) + linkLength > CharLimitsPerNetwork.GoogleSocialProfile;
};

const instagramFacebookFirstCommentCharLimitInvalid = (
  texts: CustomParagraph[],
  profileCombinations: IgFbFirstCommentProfileCombinations
) => {
  const textLength = getTextLength(texts);
  switch (profileCombinations) {
    case '':
    default:
      return false;
    case 'ig':
      return textLength > FirstCommentCharLimitsPerNetwork.InstagramSocialProfile;
    case 'fb':
      return textLength > FirstCommentCharLimitsPerNetwork.FacebookSocialProfile;
    case 'fbig':
      return (
        textLength >
        Math.min(
          FirstCommentCharLimitsPerNetwork.FacebookSocialProfile,
          FirstCommentCharLimitsPerNetwork.InstagramSocialProfile
        )
      );
  }
};

const instagramMediaLimitInvalid = (media: MediaTypePostEditorState[], varyMedia: PostMultiMediaAttachMode | null) => {
  const { invalidImages, invalidVideos } = getMediaConfigValidation('instagram', media, varyMedia);
  return invalidImages || invalidVideos;
};

const instagramTextCharLimitInvalid = (
  text: EditorText,
  link: PostEditorLink | null,
  bitlyProfile: PostEditorBitly['profilesWithBitly'][number],
  defaultBitly: PostEditorBitly['defaultBitly'],
  categoryId: string | null
) => {
  const linkLength =
    link && hasLinksChecker(text.text)
      ? calculateLinkLengthPerProfile(link, bitlyProfile, defaultBitly, categoryId)
      : 0;
  return (
    CharLimitsPerNetwork.InstagramSocialProfile &&
    getTextLength(text.text) + linkLength > CharLimitsPerNetwork.InstagramSocialProfile
  );
};

const linkedinFirstCommentCharLimitInvalid = (text: CustomParagraph[]) => {
  const textLength = getTextLength(text);
  return textLength > FirstCommentCharLimitsPerNetwork.LinkedInSocialProfile;
};

const linkedinMediaLimitInvalid = (media: MediaTypePostEditorState[], varyMedia: PostMultiMediaAttachMode | null) => {
  const { invalidImages, invalidVideos, invalidGifs } = getMediaConfigValidation('linkedin', media, varyMedia);
  return invalidImages || invalidGifs || invalidVideos;
};

const linkedinTextCharLimitInvalid = (
  text: EditorText,
  link: PostEditorLink | null,
  bitlyProfile: PostEditorBitly['profilesWithBitly'][number],
  defaultBitly: PostEditorBitly['defaultBitly'],
  categoryId: string | null
) => {
  const linkLength = link ? calculateLinkLengthPerProfile(link, bitlyProfile, defaultBitly, categoryId) : 0;
  return getTextLength(text.text) + linkLength > CharLimitsPerNetwork.LinkedInSocialProfile;
};

const mediaRequiredInvalid = (
  postMedia: MediaTypePostEditorState[],
  link: GroupPostAttributesContentType['link'],
  canUseImageFromLink: boolean
) => {
  const hasMetaImagePreview = () => {
    if (!link) return false;
    return (
      link.linkMetadata &&
      (link.linkMetadata.openGraph?.image || link.linkMetadata.twitterCard?.image) &&
      canUseImageFromLink
    );
  };

  const isValid = postMedia.length || hasMetaImagePreview();
  return !isValid;
};

const noGifsAllowedInvalid = (media: MediaTypePostEditorState[]): boolean =>
  media.some(m => m.type === MediaType.PHOTO && m.gif);

const noImagesAllowedIvalid = (media: MediaTypePostEditorState[]): boolean =>
  media.some(m => m.type === MediaType.PHOTO && !m.gif);

const noMixedMediaInvalid = (postMedia: MediaTypePostEditorState[], typePostMedia: PostMultiMediaAttachMode | null) => {
  const isAlternateMedia = typePostMedia === PostMultiMediaAttachMode.ALTERNATE;
  if (postMedia.length <= 1) return false;
  const hasPhotos = postMedia.some(m => m.type === MediaType.PHOTO && !m.gif);
  const hasGifs = postMedia.some(m => m.type === MediaType.PHOTO && m.gif);
  const hasVideos = postMedia.some(m => m.type === MediaType.VIDEO);
  if (isAlternateMedia) {
    const photosPresent = [hasPhotos, hasGifs].filter(a => a).length > 0;
    return photosPresent && hasVideos;
  }
  return [hasPhotos, hasGifs, hasVideos].filter(a => a).length > 1;
};

const noVideosAllowedInvalid = (media: MediaTypePostEditorState[]): boolean => {
  return media.some(m => m.type === MediaType.VIDEO);
};

const pinterestLineBreakLimitInvalid = (text: CustomParagraph[]) => {
  const regex = /(\r\n|\r|\n)/;
  return regex.test(serializeText(text));
};

const pinterestMediaLimitInvalid = (media: MediaTypePostEditorState[], varyMedia: PostMultiMediaAttachMode | null) => {
  const { invalidImages, invalidGifs, invalidVideos } = getMediaConfigValidation('pinterest', media, varyMedia);
  return invalidImages || invalidGifs || invalidVideos;
};

const pinterestTextCharLimitInvalid = (
  text: EditorText,
  link: PostEditorLink | null,
  bitlyProfile: PostEditorBitly['profilesWithBitly'][number],
  defaultBitly: PostEditorBitly['defaultBitly'],
  categoryId: string | null
) => {
  const linkLength = link ? calculateLinkLengthPerProfile(link, bitlyProfile, defaultBitly, categoryId) : 0;
  return !(
    CharLimitsPerNetwork.PinterestSocialProfile &&
    getTextLength(text.text) + linkLength <= CharLimitsPerNetwork.PinterestSocialProfile
  );
};

const preventAudioOnlyFilesInvalid = (media: MediaTypePostEditorState[]): boolean => {
  return media.reduce((prev, current) => {
    if (prev) return true;
    return current.type === 'VIDEO' && (current.height === 0 || current.width === 0);
  }, false);
};

const tikTokMediaLimitInvalid = (media: MediaTypePostEditorState[], varyMedia: PostMultiMediaAttachMode | null) => {
  const { invalidVideos } = getMediaConfigValidation('tiktok', media, varyMedia);
  return invalidVideos;
};

const tikTokTextCharLimitInvalid = (
  text: EditorText,
  link: PostEditorLink | null,
  bitlyProfile: PostEditorBitly['profilesWithBitly'][number],
  defaultBitly: PostEditorBitly['defaultBitly'],
  categoryId: string | null
) => {
  const linkLength =
    link && hasLinksChecker(text.text)
      ? calculateLinkLengthPerProfile(link, bitlyProfile, defaultBitly, categoryId)
      : 0;
  return (
    CharLimitsPerNetwork.TikTokSocialProfile &&
    getTextLength(text.text) + linkLength > CharLimitsPerNetwork.TikTokSocialProfile
  );
};

const tikTokVideoLengthInvalid = (media: MediaTypePostEditorState[]): boolean => {
  const videos = media.filter(m => m.type === MediaType.VIDEO);
  return videos.some(video => video.length === null || video.length < 3 || video.length > 600); // video must be longer than 3 sec and shorther than 10 mins
};

const tikTokVideoSizeInvalid = (media: MediaTypePostEditorState[]): boolean => {
  const videos = media.filter(m => m.type === MediaType.VIDEO);
  return videos.some(video => video.width && video.height && (video.height < 360 || video.width < 360)); // video must be longer than 3 sec and shorther than 10 mins
};

const twitterMediaLimitInvalid = (media: MediaTypePostEditorState[], varyMedia: PostMultiMediaAttachMode | null) => {
  const { invalidImages, invalidVideos, invalidGifs } = getMediaConfigValidation('twitter', media, varyMedia);
  return invalidImages || invalidVideos || invalidGifs;
};

const twitterTextCharLimitInvalid = (
  text: EditorText,
  link: PostEditorLink | null,
  bitlyProfile: PostEditorBitly['profilesWithBitly'][number],
  defaultBitly: PostEditorBitly['defaultBitly'],
  categoryId: string | null
) => {
  const linkLength =
    link && hasLinksChecker(text.text)
      ? calculateLinkLengthPerProfile(link, bitlyProfile, defaultBitly, categoryId)
      : 0;
  return getTextLength(text.text) + linkLength > CharLimitsPerNetwork.TwitterSocialProfile;
};

const videoNotTranscodedInvalid = (media: MediaTypePostEditorState[], postTiming: PostTiming) => {
  const hasInvalidVideos = media.filter(m => m.type === 'VIDEO' && !m.transcoded);
  return postTiming === PostTiming.NOW && hasInvalidVideos.length;
};

export const SingularValidator = {
  checkExpiryCountInvalid,
  checkExpirtyDateInvalid,
  checkGoogleButtonLinkInvalid,
  checkLinkPreviewSuppressedInvalid,
  checkLinksPresentInvalid,
  expireDateCountRequiredInvalid,
  facebookContentRequiredInvalid,
  facebookMediaLimitInvalid,
  facebookTextCharLimitInvalid,
  facebookTwitterLineBreakLimitInvalid,
  googleMediaLimitInvalid,
  googleMinMediaInvalid,
  googleTextCharLimitInvalid,
  instagramFacebookFirstCommentCharLimitInvalid,
  instagramMediaLimitInvalid,
  instagramTextCharLimitInvalid,
  linkedinFirstCommentCharLimitInvalid,
  linkedinMediaLimitInvalid,
  linkedinTextCharLimitInvalid,
  mediaRequiredInvalid,
  noGifsAllowedInvalid,
  noImagesAllowedIvalid,
  noMixedMediaInvalid,
  noVideosAllowedInvalid,
  pinterestLineBreakLimitInvalid,
  pinterestMediaLimitInvalid,
  pinterestTextCharLimitInvalid,
  preventAudioOnlyFilesInvalid,
  tikTokMediaLimitInvalid,
  tikTokTextCharLimitInvalid,
  tikTokVideoLengthInvalid,
  tikTokVideoSizeInvalid,
  twitterMediaLimitInvalid,
  twitterTextCharLimitInvalid,
  videoNotTranscodedInvalid
} as const;
